import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  LayoutDashboard,
  Car,
  Users,
  Building2,
  TrendingUp,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronLeft,
  User,
  Calendar,
  Wallet,
  CheckSquare,
  BarChart3,
  Calculator,
  PieChart,
  MessageCircle,
  Link2,
  CreditCard,
  Receipt,
  Landmark,
  ArrowLeftRight,
  Plug,
  RefreshCw,
  Megaphone,
  Code,
  Globe,
} from 'lucide-react'

interface LayoutProps {
  children: React.ReactNode
}

interface NavSection {
  id: string
  label: string
  items: NavItem[]
  adminOnly?: boolean
  gerenteOnly?: boolean
}

interface NavItem {
  path: string
  label: string
  icon: React.ElementType
  adminOnly?: boolean
  gerenteOnly?: boolean
}

// Definição das seções de navegação
const getNavSections = (isAdmin: boolean, isGerente: boolean): NavSection[] => {
  const sections: NavSection[] = [
    {
      id: 'principal',
      label: 'Principal',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/relatorios', label: 'Relatórios', icon: PieChart },
        { path: '/veiculos', label: 'Veículos', icon: Car },
        { path: '/clientes', label: 'Clientes', icon: Users },
        { path: '/fornecedores', label: 'Fornecedores', icon: Building2 },
      ],
    },
    {
      id: 'comercial',
      label: 'Comercial',
      items: [
        { path: '/crm', label: 'Dashboard CRM', icon: BarChart3 },
        { path: '/crm/leads', label: 'Funil de Vendas', icon: TrendingUp },
        { path: '/crm/tarefas', label: 'Tarefas', icon: CheckSquare },
        { path: '/vendas', label: 'Vendas', icon: FileText },
        { path: '/simulador-financiamento', label: 'Simulador', icon: Calculator },
        { path: '/agenda', label: 'Agenda', icon: Calendar },
      ],
    },
    {
      id: 'financeiro',
      label: 'Financeiro',
      adminOnly: true,
      items: [
        { path: '/financeiro', label: 'Dashboard Financeiro', icon: Wallet },
        { path: '/financeiro/contas-pagar', label: 'Contas a Pagar', icon: CreditCard },
        { path: '/financeiro/contas-receber', label: 'Contas a Receber', icon: Receipt },
        { path: '/financeiro/fluxo-caixa', label: 'Fluxo de Caixa', icon: ArrowLeftRight },
        { path: '/financeiro/contas-bancarias', label: 'Contas Bancárias', icon: Landmark },
      ],
    },
    {
      id: 'integracoes',
      label: 'Integrações',
      items: [
        { path: '/integracoes', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/integracoes/plataformas', label: 'Plataformas', icon: Plug },
        { path: '/integracoes/publicacoes', label: 'Publicações', icon: Megaphone },
        { path: '/integracoes/sincronizacao', label: 'Sincronização', icon: RefreshCw },
        { path: '/integracoes/xml-feed', label: 'XML Feed', icon: Code },
      ],
    },
    {
      id: 'sistema',
      label: 'Sistema',
      gerenteOnly: true,
      items: [
        { path: '/configuracoes', label: 'Configurações', icon: Settings, adminOnly: true },
        { path: '/configuracoes/site', label: 'Site Próprio', icon: Globe, adminOnly: true },
        { path: '/configuracoes/revendamais', label: 'RevendaMais', icon: Link2, gerenteOnly: true },
        { path: '/chat', label: 'Chat ao Vivo', icon: MessageCircle },
        { path: '/configuracoes/whatsapp', label: 'WhatsApp', icon: MessageCircle, adminOnly: true },
      ],
    },
  ]

  // Filtrar seções e itens por permissão
  return sections
    .filter(section => {
      if (section.adminOnly && !isAdmin) return false
      if (section.gerenteOnly && !isAdmin && !isGerente) return false
      return true
    })
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (item.adminOnly && !isAdmin) return false
        if (item.gerenteOnly && !isAdmin && !isGerente) return false
        return true
      }),
    }))
    .filter(section => section.items.length > 0)
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false) // Mobile sidebar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false) // Desktop collapsed
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [expandedSections, setExpandedSections] = useState<string[]>(['principal'])
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const isAdmin = user?.tipoUsuario === 'ADMIN'
  const isGerente = user?.tipoUsuario === 'GERENTE'
  const navSections = useMemo(() => getNavSections(isAdmin, isGerente), [isAdmin, isGerente])

  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024)
      if (window.innerWidth > 1024) {
        setSidebarOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Fechar menu do usuário ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userMenuOpen])

  // Auto-expandir seção baseada na página atual
  useEffect(() => {
    const currentPath = location.pathname
    for (const section of navSections) {
      if (section.items.some(item =>
        currentPath === item.path || currentPath.startsWith(item.path + '/')
      )) {
        setExpandedSections([section.id])
        return
      }
    }
    setExpandedSections(['principal'])
  }, [location.pathname, navSections])

  const handleLogout = async () => {
    await logout()
    window.location.href = '/login'
  }

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId) ? [] : [sectionId]
    )
  }

  // Coleta todos os paths de menu para verificação
  const allMenuPaths = useMemo(() =>
    navSections.flatMap(s => s.items.map(i => i.path)),
    [navSections]
  )

  const isItemActive = (path: string) => {
    const currentPath = location.pathname

    // Match exato
    if (currentPath === path) return true

    // Se não começa com o path + '/', não é ativo
    if (!currentPath.startsWith(path + '/')) return false

    // Se começa com este path, verifica se existe um item de menu mais específico
    // Por exemplo: se estamos em /crm/leads e verificando /crm,
    // /crm/leads é mais específico, então /crm não deve ficar ativo
    const hasMoreSpecificMatch = allMenuPaths.some(menuPath =>
      menuPath !== path &&
      menuPath.length > path.length &&
      (currentPath === menuPath || currentPath.startsWith(menuPath + '/'))
    )

    return !hasMoreSpecificMatch
  }

  const handleNavClick = () => {
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64'

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-gray-900 transform transition-all duration-300 ease-in-out ${
          isMobile
            ? sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64'
            : `lg:translate-x-0 ${sidebarWidth}`
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
          <Link to="/" className="flex items-center gap-2" onClick={handleNavClick}>
            <img
              src="/assets/logo-icon.png"
              alt="AutoGestão"
              className="w-8 h-8 object-contain flex-shrink-0"
            />
            {!sidebarCollapsed && (
              <span className="text-xl font-bold text-white">AutoGestão</span>
            )}
          </Link>
          {isMobile ? (
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          ) : (
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="text-gray-400 hover:text-white transition-all duration-200"
              title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              <ChevronLeft
                className={`w-5 h-5 transition-transform duration-200 ${
                  sidebarCollapsed ? 'rotate-180' : ''
                }`}
              />
            </button>
          )}
        </div>

        {/* Menu Items */}
        <nav className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px - 90px)' }}>
          {navSections.map(section => {
            const isExpanded = expandedSections.includes(section.id)
            const hasActiveItem = section.items.some(item => isItemActive(item.path))

            return (
              <div key={section.id} className="mb-1">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    hasActiveItem
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <span className={sidebarCollapsed ? 'hidden' : ''}>{section.label}</span>
                  {!sidebarCollapsed && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                    />
                  )}
                </button>

                {/* Section Items */}
                {(isExpanded || sidebarCollapsed) && (
                  <div className={`mt-1 space-y-1 ${sidebarCollapsed ? '' : 'ml-2'}`}>
                    {section.items.map(item => {
                      const Icon = item.icon
                      const isActive = isItemActive(item.path)

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={handleNavClick}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                          }`}
                          title={sidebarCollapsed ? item.label : undefined}
                        >
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          {!sidebarCollapsed && <span className="text-sm">{item.label}</span>}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* User Info Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold">
                {user?.nome?.charAt(0).toUpperCase()}
              </span>
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.nome}</p>
                <p className="text-xs text-gray-400 truncate">{user?.tipoUsuario}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${isMobile ? '' : sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between h-full px-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Loja Name */}
            <div className="hidden lg:block">
              <h1 className="text-lg font-semibold text-gray-900">
                {user?.lojaNome || 'AutoGestão'}
              </h1>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {user?.nome?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden md:block text-sm font-medium">{user?.nome}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <Link
                      to="/perfil"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <User className="w-4 h-4" />
                      Meu Perfil
                    </Link>
                    <Link
                      to="/configuracoes"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Settings className="w-4 h-4" />
                      Configurações
                    </Link>
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" />
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">{children}</main>
      </div>
    </div>
  )
}
